import { Box, CircularProgress, keyframes, Stack, SxProps, Theme } from '@mui/material';
import { stableStringify } from '@provision/formatting-utils';
import { useEffect, useRef, useState } from 'react';
import { DynamicIcon, Tooltip } from 'src/components';
import { FormLabel } from 'src/components/Forms/FormLabel/form-label';
import {
  CustomAttributeValue,
  NormalizedCustomAttributeDefinition,
} from 'src/graphql/entities/custom-attributes';
import { getCustomAttributeConfig } from 'src/graphql/entities/custom-attributes/field-view-registry';
import { CustomAttributeRowData } from 'src/graphql/entities/custom-attributes/field-view-registry/types';
import { useSelectedPartner } from 'src/graphql/entities/partner/partner';
import { useDebouncedValue } from 'src/utils/hooks/use-debounced-value';
import { useDelayedFlag } from 'src/utils/hooks/use-delayed-flag';

interface CustomAttributeRowProps {
  row: CustomAttributeRowData;
  onSave: (
    definition: NormalizedCustomAttributeDefinition,
    value: CustomAttributeValue,
  ) => void;
  disabled: boolean;
}

const valueFadeIn = keyframes`
  from { opacity: 0.35; }
  to { opacity: 1; }
`;

const SPINNER_DELAY_MS = 250;
const SAVE_DEBOUNCE_MS = 300;

const ROW_STYLES: SxProps<Theme> = (theme) => ({
  minHeight: '2rem',
  '& .MuiOutlinedInput-root': {
    fontSize: '0.8rem',
  },
  '& .MuiOutlinedInput-input:not(.MuiInputBase-inputMultiline)': {
    paddingTop: '0.25rem',
    paddingBottom: '0.25rem',
  },
  '& .MuiAutocomplete-inputRoot.MuiOutlinedInput-root': {
    paddingTop: 0,
    paddingBottom: 0,
  },
  // mobile Safari zooms when focusing inputs with computed font-size below ~16px;
  // force the editor font-size up at the `sm` breakpoint to prevent the auto-zoom.
  [theme.breakpoints.down('sm')]: {
    '& .MuiInputBase-input': {
      fontSize: theme.utils?.important?.('1rem') ?? '1rem !important',
    },
  },
});

const VALUE_SIDE_STYLES = {
  borderRadius: 1,
  transition: 'background-color 150ms ease',
  '&:hover': {
    backgroundColor: 'custom.OV.sys.surface',
  },
};

export const CustomAttributeRow = ({ row, onSave, disabled }: CustomAttributeRowProps) => {
  const { definition } = row;
  const { selectedPartner } = useSelectedPartner();
  const field = getCustomAttributeConfig(definition.type);
  const canEdit = !disabled && field.editable;
  const isDirectEdit = canEdit && field.editable && field.directEdit === true;
  const [isEditing, setIsEditing] = useState(false);
  const isInteractive = canEdit && !isEditing && !isDirectEdit;
  const [draft, setDraft] = useState(row.parsedValue);
  const triggerRef = useRef<HTMLDivElement>(null);
  const showSpinner = useDelayedFlag(row.isSaving, SPINNER_DELAY_MS);

  // Hold the user's latest intended value and only fire the API call once
  // they stop interacting. The pending value is rendered optimistically so the
  // UI updates immediately, and is cleared once the save completes.
  const [pendingValue, setPendingValue] = useState<CustomAttributeValue | null>(null);
  const debouncedPending = useDebouncedValue(pendingValue, SAVE_DEBOUNCE_MS);

  useEffect(() => {
    if (debouncedPending !== null) {
      onSave(definition, debouncedPending);
    }
    // onSave/definition intentionally omitted — we only want to react to debounced changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPending]);

  const wasSavingRef = useRef(false);
  useEffect(() => {
    if (wasSavingRef.current && !row.isSaving) {
      setPendingValue(null);
    }
    wasSavingRef.current = row.isSaving;
  }, [row.isSaving]);

  const displayedValue = pendingValue ?? row.parsedValue;

  const startEdit = () => {
    setDraft(displayedValue);
    setIsEditing(true);
  };

  const closeEdit = () => {
    setIsEditing(false);
    queueMicrotask(() => triggerRef.current?.focus());
  };

  const submit = (value: CustomAttributeValue) => {
    if (!isDirectEdit) {
      closeEdit();
    }
    setPendingValue(value);
  };

  const { View } = field;
  const timezone = selectedPartner?.timeZone || undefined;
  const Editor = (isEditing || isDirectEdit) && field.editable ? field.Editor : null;
  const verticalLayout = field.verticalLayout === true;
  const editorValue = isDirectEdit ? displayedValue : draft;

  // While a save is in flight, swallow pointer events on the editor surface so
  // rapid clicks don't queue multiple saves — without any greyed-out styling.
  const busyOverlaySx = row.isSaving ? { pointerEvents: 'none' as const } : null;

  const valueBox = (
    <Box
      ref={triggerRef}
      sx={{
        flexGrow: 1,
        minWidth: 0,
        cursor: isInteractive ? 'pointer' : 'default',
        ...busyOverlaySx,
      }}
      onClick={isInteractive ? startEdit : undefined}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : -1}
      aria-label={isInteractive ? `Edit ${definition.label}` : undefined}
      onKeyDown={
        isInteractive
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                startEdit();
              }
            }
          : undefined
      }
    >
      {Editor ? (
        <Editor
          value={editorValue}
          definition={row.definition}
          timezone={timezone}
          onChange={setDraft}
          onSave={(next) => submit(next ?? editorValue)}
          onCancel={closeEdit}
          disabled={disabled}
        />
      ) : (
        <Box
          key={stableStringify(displayedValue)}
          sx={{ animation: `${valueFadeIn} 150ms ease` }}
        >
          <View value={displayedValue} definition={row.definition} timezone={timezone} />
        </Box>
      )}
    </Box>
  );

  const sideAdornments = (
    <>
      {showSpinner ? <CircularProgress size="0.875rem" /> : null}
      {row.error ? (
        <Tooltip title={row.error}>
          <Box display="inline-flex">
            <DynamicIcon name="error" sx={{ color: 'error.main', fontSize: '1rem' }} />
          </Box>
        </Tooltip>
      ) : null}
    </>
  );

  if (verticalLayout) {
    return (
      <Stack px={2} gap={0.25} sx={ROW_STYLES}>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <FormLabel label={definition.label} />
          <Box sx={{ marginLeft: 'auto' }} />
          {sideAdornments}
        </Stack>
        <Box sx={VALUE_SIDE_STYLES}>{valueBox}</Box>
      </Stack>
    );
  }

  return (
    <Stack direction="row" alignItems="center" px={2} sx={ROW_STYLES}>
      <FormLabel label={definition.label} />
      <Stack
        direction="row"
        alignItems="center"
        gap={0.5}
        sx={{ width: '100%', px: '0.5rem', ...VALUE_SIDE_STYLES }}
      >
        {valueBox}
        {sideAdornments}
      </Stack>
    </Stack>
  );
};
